/**
 * Soft 401 handler — shared by every UI consuming @agion/auth.
 *
 * Replaces the per-app pattern of immediately doing
 * `window.location.href = '/login'` on any 401 response. That pattern bounces
 * the user out of an active session whenever a single request fails for
 * non-authentication reasons (race during silent refresh, stale token on a
 * long-running request, transient backend hiccup, etc.).
 *
 * Behavior:
 *   1. Consult auth-service GET /session as the canonical source of truth
 *      for "am I still logged in?". This is the same probe AuthProvider uses.
 *   2. If the session is still authenticated, return 'authenticated' — the
 *      caller surfaces the original error so retry logic (TanStack Query etc.)
 *      can take over and the user stays in their workflow.
 *   3. If the session is genuinely gone, clear local auth state and redirect
 *      to the configured login URL. Returns 'unauthenticated'.
 *
 * Concurrency: many requests can fail in parallel during a brief outage.
 * A single in-flight Promise is shared across callers so we never hammer
 * /session with N parallel probes. The first 401 wins, all others await it.
 */

import { useAuth } from './store'
import type { AuthUser, SessionResponse } from './types'

let inFlight: Promise<UnauthorizedResult> | null = null

export type UnauthorizedResult = 'authenticated' | 'unauthenticated'

/**
 * Confirm session state with auth-service, then either keep the user in
 * place ('authenticated') or clear local state and redirect to login
 * ('unauthenticated').
 *
 * Idempotent under concurrent invocation.
 */
export function handleUnauthorized(): Promise<UnauthorizedResult> {
  if (inFlight) return inFlight

  inFlight = (async (): Promise<UnauthorizedResult> => {
    // Yield once before doing any work. Without this, an IIFE that returns
    // synchronously (e.g. via the early `return 'unauthenticated'` below)
    // would run its `finally` BEFORE the outer `inFlight = …` assignment
    // lands, leaving inFlight pointing at a stale resolved promise that
    // would short-circuit every subsequent call. The microtask yield lets
    // the assignment complete first, so finally correctly clears it.
    await Promise.resolve()

    try {
      const state = useAuth.getState()
      const client = state.client
      const config = state.config

      if (!client || !config) {
        // Auth not wired up — most likely a logout-in-flight or a race
        // during initial mount. Treat as unauthenticated to be safe.
        return 'unauthenticated'
      }

      // Short-circuit if auth state is already cleared. A prior handleUnauthorized
      // call or the auth store's own validation already reset the session;
      // the React tree's <Unauthenticated> component is handling the redirect via
      // initiateSSO(). Firing another window.location.href here would race with that
      // OIDC redirect, abort it mid-flight (ERR_ABORTED), and lose the return_to path.
      if (!state.isAuthenticated) {
        return 'unauthenticated'
      }

      const result = await client.checkSession()
      if (result.status === 'ok') {
        // Session still alive on the server. Keep local state in sync —
        // the user / org / role may have changed since last probe — and
        // hand the original error back to the caller.
        useAuth.getState().setAuth(result.user, result.sessionData)
        return 'authenticated'
      }

      if (result.status === 'unavailable') {
        // The sign-in service could not answer (5xx, network error): the
        // session is unconfirmed, not gone. Same as the catch path below.
        console.warn('[auth] handleUnauthorized: sign-in service unavailable, treating as transient')
        return 'authenticated'
      }

      // Confirmed unauthenticated. Reset auth state — this synchronously
      // triggers React to render <Unauthenticated> → <SSORedirect> →
      // initiateSSO(), which navigates via the proper OIDC flow (with
      // return_to preserved). Direct window.location.href here would race
      // with that redirect and abort it every time.
      useAuth.getState().reset()
      return 'unauthenticated'
    } catch (err) {
      // Network failure, auth-service down, etc. We do NOT redirect on
      // unconfirmed failure — that would replicate the bug we're fixing.
      // Surface as 'authenticated' so the caller's normal retry/error path
      // runs; if the session really is gone, the next probe will catch it.
      console.warn('[auth] handleUnauthorized: probe failed, treating as transient', err)
      return 'authenticated'
    } finally {
      inFlight = null
    }
  })()

  return inFlight
}

/**
 * Test-only helper: reset the in-flight singleton between test cases.
 * Not exported from the package index — only the tests reach for it.
 */
export function __resetHandleUnauthorizedForTests(): void {
  inFlight = null
}

/** Re-export for typing convenience in consumer test files. */
export type { AuthUser, SessionResponse }
